import { useGetAnalyticsAggregatedDataForCampaignDashboard } from "@/features/campaigns/hooks/useCampaign";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import {
  CalendarDays,
  Linkedin,
  Mail,
  MessageCircle,
  UserCheck,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BorderCard from "../components/BorderCard";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CardTitle from "../components/CardTitle";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import CampaignInsightCard from "../components/CampaignInsightCard";
import ChannelViewModal from "./ChannelViewModal";
import ChannelAnalyticsCard from "./ChannelAnalyticsCard";
import { buildContactAddedInsightDescription } from "@/features/campaigns/utils/analyticsInsightDescriptions";

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toInclusiveApiRange = (from, to) => {
  const fromDate = new Date(`${from}T00:00:00`);
  const toExclusive = new Date(`${to}T00:00:00`);
  toExclusive.setDate(toExclusive.getDate() + 1);
  return { from: fromDate.toISOString(), to: toExclusive.toISOString() };
};

const responsePercentage = (responseCount, totalSteps) => {
  const total = Number(totalSteps) || 0;
  if (total === 0) return "0%";
  return `${(((Number(responseCount) || 0) / total) * 100).toFixed(2)}%`;
};

const AnalyticsPerformanceInsightArea = ({ campaignStatus }) => {
  const [viewChannel, setViewChannel] = useState(null);
  const [action, setAction] = useState(null);
  const [channel, setChannel] = useState(null);
  const [preset, setPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
    label: "All time",
  });
  const navigate = useNavigate();
  const campaignId = useCampaignId();
  const { data: analyticsAggregatedData } =
    useGetAnalyticsAggregatedDataForCampaignDashboard(
      campaignId,
      dateRange.from,
      dateRange.to,
    );

  const today = toDateInputValue(new Date());

  const handlePresetChange = (value) => {
    setPreset(value);
    if (value === "custom") return;
    if (value === "all") {
      setDateRange({ from: undefined, to: undefined, label: "All time" });
      return;
    }

    if (value === "today" || value === "yesterday") {
      const date = new Date();
      if (value === "yesterday") date.setDate(date.getDate() - 1);
      const dateValue = toDateInputValue(date);
      setDateRange({
        ...toInclusiveApiRange(dateValue, dateValue),
        label: value === "today" ? "Today" : "Yesterday",
      });
      return;
    }

    const days = Number(value);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    const fromValue = toDateInputValue(from);
    const toValue = toDateInputValue(to);
    setDateRange({
      ...toInclusiveApiRange(fromValue, toValue),
      label: `Last ${days} days`,
    });
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo || customFrom > customTo) return;
    setDateRange({
      ...toInclusiveApiRange(customFrom, customTo),
      label: `${customFrom} – ${customTo}`,
    });
  };

  const analytics = analyticsAggregatedData?.analytics ?? {};
  const totalContacts = analyticsAggregatedData?.totalContacts ?? 0;
  const email = analytics.email ?? {};
  const linkedin = analytics.linkedin ?? {};
  const whatsapp = analytics.whatsapp ?? {};

  const channelCards = [
    {
      title: "Email",
      Icon: Mail,
      iconClassName: "text-error-500",
      iconBackgroundClassName: "bg-error-500/15",
      metrics: [
        { label: "Sent", value: email.sent, action: "Sent" },
        { label: "Opened", value: email.opened, action: "Opened" },
        { label: "Clicked", value: email.clicked, action: "Clicked" },
        { label: "Responded", value: email.responded, action: "Responded" },
        { label: "Positive replies", value: email.positiveReplies },
        { label: "Automated replies", value: email.autoReplies },
        { label: "Delivery failures", value: email.deliveryFailures },
        { label: "Unclassified replies", value: email.unknownReplies },
      ],
      primaryMetric: {
        label: "Total Steps",
        value: email.totalSteps,
        percentage: responsePercentage(email.responded, email.totalSteps),
        engagementLabel: "Reply Engagement",
      },
      channel: "email",
    },
    {
      title: "LinkedIn",
      Icon: Linkedin,
      iconClassName: "text-secondary",
      iconBackgroundClassName: "bg-secondary/15",
      metrics: [
        {
          label: "Connection Sent",
          value: linkedin.connectionRequestsSent,
          action: "Requests Sent",
        },
        {
          label: "Connection Failed",
          value: linkedin.connectionRequestsFailed,
          action: "Requests Failed",
        },
        {
          label: "Connection Accepted",
          value: linkedin.connectionRequestsAccepted,
          action: "Requests Accepted",
        },
        { label: "Message Sent", value: linkedin.messageSent, action: "Sent" },
        { label: "Opened", value: linkedin.opened, action: "Opened" },
        { label: "Responded", value: linkedin.responded, action: "Responded" },
      ],
      primaryMetric: {
        label: "Total Steps",
        value: linkedin.totalSteps,
        percentage: responsePercentage(linkedin.responded, linkedin.totalSteps),
        engagementLabel: "Reply Engagement",
      },
      channel: "linkedin",
    },
    {
      title: "WhatsApp",
      Icon: MessageCircle,
      iconClassName: "text-success-500",
      iconBackgroundClassName: "bg-success-500/15",
      metrics: [
        { label: "Sent", value: whatsapp.sent, action: "Sent" },
        { label: "Opened", value: whatsapp.opened, action: "Opened" },
        { label: "Responded", value: whatsapp.responded, action: "Responded" },
      ],
      primaryMetric: {
        label: "Total Steps",
        value: whatsapp.totalSteps,
        percentage: responsePercentage(whatsapp.responded, whatsapp.totalSteps),
        engagementLabel: "Reply Engagement",
      },
      channel: "whatsapp",
    },
  ];

  const handleViewLeadButtonClick = () => {
    navigate(`/campaigns/${campaignId}/leads`);
  };

  const handleView = (channelKey, actionLabel) => {
    setAction(actionLabel);
    setChannel(channelKey);
    setViewChannel(true);
  };

  return (
    <div>
      <BorderCard>
        <CardTitle text={"Performance Insights"} description={dateRange.label} />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <CommonSelect
            value={preset}
            onChange={handlePresetChange}
            options={[
              { value: "all", label: "All time" },
              { value: "today", label: "Today" },
              { value: "yesterday", label: "Yesterday" },
              { value: "7", label: "Last 7 days" },
              { value: "30", label: "Last 30 days" },
              { value: "90", label: "Last 90 days" },
              { value: "custom", label: "Custom range" },
            ]}
            width="w-[140px]"
            startContent={<CalendarDays size={15} className="text-gray-500 mr-1 flex-shrink-0" />}
            ariaLabel="Performance insights date range"
          />
          {preset === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customFrom}
                max={customTo || today}
                onChange={(event) => setCustomFrom(event.target.value)}
                className="rounded-lg border border-gray-300 px-2.5 py-2 text-xs text-gray-700"
                aria-label="From date"
              />
              <span className="text-xs text-gray-500">to</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                max={today}
                onChange={(event) => setCustomTo(event.target.value)}
                className="rounded-lg border border-gray-300 px-2.5 py-2 text-xs text-gray-700"
                aria-label="To date"
              />
              <button
                type="button"
                onClick={applyCustomRange}
                disabled={!customFrom || !customTo || customFrom > customTo}
                className="rounded-lg bg-secondary px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          )}
          <SecondaryButton
            title={"View All Leads"}
            onClick={handleViewLeadButtonClick}
          />
        </div>
      </BorderCard>
      <BorderCard variant="bottom">
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="min-w-0">
            <CampaignInsightCard
              index={0}
              title="Contact Added"
              value={`${totalContacts}`}
              total={`${totalContacts}`}
              icon={
                <UserCheck
                  className="text-success-500"
                  size={14}
                  strokeWidth={2}
                />
              }
              description={buildContactAddedInsightDescription(totalContacts)}
              textColor="text-success-500"
              campaignStatus={campaignStatus}
              expandable={false}
            />
          </div>
          {channelCards.map((card) => (
            <ChannelAnalyticsCard
              key={card.title}
              title={card.title}
              Icon={card.Icon}
              iconClassName={card.iconClassName}
              iconBackgroundClassName={card.iconBackgroundClassName}
              metrics={card.metrics}
              primaryMetric={card.primaryMetric}
              onView={(actionLabel) => handleView(card.channel, actionLabel)}
            />
          ))}
        </div>
      </BorderCard>
      {viewChannel && (
        <ChannelViewModal
          isOpen={viewChannel}
          onClose={() => setViewChannel(null)}
          action={action}
          channel={channel}
          from={dateRange.from}
          to={dateRange.to}
        />
      )}
    </div>
  );
};

export default AnalyticsPerformanceInsightArea;
